/**
 * This file contains all of the functions that will be exposed to external parties. A subset of these will be selected
 * by one of the Interface files (ClientAPI, Studio, etc.), and each of those functions will be properties of the
 * AyvriViewer object. The ClientAPI functions will also be exposed to a parent window via the IFrameAPI.
 *
 * Each exported function obeys a strict interface:
 *  someFunction(options: Object): Promise<[Object | undefined] | Error>
 * In the case where the Promise is rejected, the error message string is returned. Otherwise the function is free to
 * resolve with nothing or an object.
 *
 * The options object needs to go through much stricter property verification than usual. Several helpers such as
 * `getRequiredParam()` are supplied to help reduce boilerplate while doing these checks.
 */
import cuid from 'cuid';

import validateMedia from 'ayvri-vera/lib/media';
import validateActivity from 'ayvri-vera/lib/activity';
import validateGpsPoint from 'ayvri-vera/lib/gpsPoint';
import validateSegment from 'ayvri-vera/lib/segment';

import * as Actions from '../Actions';
import { getApp } from '../App';
import { defined, defaultValue, ISOtoMicroseconds, convertToRadian } from '../helpers';
import PlayerState from '../PlayerState';

/**************************************************************************************************************
/* ExternalEvents 
/* These may be listened to by external parties. Use eventEmitter.on() to listen for events.
/* This file is intended mainly as documentation; it describes the name and parameters of each possible event.
/*************************************************************************************************************/
export function onceSceneLoaded() {
  return new Promise(resolve => {
    if (getApp().store.getState().loading.scene.complete) resolve();
    else getApp().eventEmitter.on('scene.load', () => resolve());
  });
}

export function oncePlayerReady() {
  return new Promise(resolve => {
    if (getApp().store.getState().loading.playerState >= PlayerState.START_SCREEN) resolve();
    else getApp().eventEmitter.on('player.ready', () => resolve());
  });
}

export function oncePlayerPlaying() {
  return new Promise(resolve => {
    if (getApp().store.getState().loading.playerState >= PlayerState.PLAYING) resolve();
    else getApp().eventEmitter.on('player.playing', () => resolve());
  });
}

export async function onClockChange(options) {
  const callback = getRequiredParam(options, 'callback');
  getApp().eventEmitter.on('clock.change', callback);
}

export async function onClockPlayPause(options) {
  const callback = getRequiredParam(options, 'callback');
  getApp().eventEmitter.on('clock.playing', callback);
}

export async function onClockSpeedChange(options) {
  const callback = getRequiredParam(options, 'callback');
  getApp().eventEmitter.on('clock.speed', callback);
}

export async function onPointsAdded(options) {
  const callback = getRequiredParam(options, 'callback');
  getApp().eventEmitter.on('track.pointsAdded', callback);
}

export async function onCameraDistanceChange(options) {
  const callback = getRequiredParam(options, 'callback');
  getApp().eventEmitter.on('camera.distanceChanged', callback);
}

/**********************************************************************************************************************
/* Actions
/**********************************************************************************************************************/

/* Segments ***********************************************************************************************************/
function verifySegmentProperties(options, store) {
  if (defined(options.target)) assert(options.target === 'activity');
  if (defined(options.targetId)) {
    assert(store.getState().activities[options.targetId], 'activityId is not a valid Activity');
  }

  if (defined(options.speed)) {
    assert(Number.isFinite(options.speed) && options.speed > 0, 'speed must be a positive finite number');
  }

  if (defined(options.targetDistance)) {
    assert(
      Number.isFinite(options.targetDistance) && options.targetDistance > 0,
      'targetDistance must be a positive finite number',
    );
  }
}

export async function addSegment(options = {}) {
  getRequiredParam(options, 'time');
  const store = getApp().store;
  const findSegmentId = segmentId => store.getState().segments.find(m => m.segmentId === segmentId);

  // If segmentId was not specified, generate a unique one
  const segmentId = defaultValue(options.segmentId, cuid());
  assert(!defined(findSegmentId(segmentId)), 'The specified segmentId already exists');
  const segOptions = { ...options, segmentId };

  // Validate that the options satifies the Vera schema
  const result = validateSegment(segOptions);
  assert(result === true, `Invalid Segment parameters: ${result}`);

  verifySegmentProperties(options, store);

  store.dispatch(Actions.addSegments([segOptions]));
  return { segmentId };
}

export async function editSegment(options = {}) {
  const store = getApp().store;
  const segmentId = getRequiredParam(options, 'segmentId');
  const segment = store.getState().segments.find(m => m.segmentId === segmentId);
  assert(defined(segment), 'The specified segment does not exist');

  verifySegmentProperties(options, store);

  getApp().store.dispatch(Actions.editSegment(options));
}

export async function deleteSegment(options = {}) {
  const store = getApp().store;
  const segmentId = getRequiredParam(options, 'segmentId');
  const segment = store.getState().segments.find(m => m.segmentId === segmentId);
  assert(defined(segment), 'The specified segment does not exist');

  getApp().store.dispatch(Actions.deleteSegment(segmentId));
}

/* Activities *********************************************************************************************************/
// @TODO: The Docs for color should specify that rgb(r,g,b) is in the 0-256 range (not 0-1);
// @TODO: Opacity should be removed from the documentation
// @TODO: Fix avatar documentation formating
// @TODO: Color should be returned as a hex string ('#FFAABB'). It's much easier to parse
export async function addActivity(options = {}) {
  getRequiredParam(options, 'activityType');

  const store = getApp().store;
  const activityCount = Object.keys(store.getState().activities).length;

  // If an activityId was not specified, generate a unique one
  const activityId = defaultValue(options.activityId, cuid());
  assert(!defined(store.getState().activities[activityId]), 'The provided activityId already exists');

  // Validate that the options satifies the Vera schema
  const activityOptions = { ...options, activityId };
  const result = validateActivity('api')(activityOptions);
  assert(result === true, `Invalid Activity parameters: ${result}`);

  // Add the Activity to state and verify that it was created successfully
  store.dispatch(Actions.addActivity(activityOptions, activityCount));
  const activity = store.getState().activities[activityId];
  assert(defined(activity), 'An unknown error occurred. The Activity could not be added');
  return { activityId: activity.activityId, color: activity.color };
}

export async function deleteActivity(options = {}) {
  const store = getApp().store;
  const activityId = getRequiredParam(options, 'activityId');
  assert(defined(store.getState().activities[options.activityId]), 'The specified activityId does not exist');
  assert(
    activityId !== store.getState().tracks.activeTrack,
    'Cannot delete the target activity. Change the target with `setActivityTarget` before deleting',
  );

  store.dispatch(Actions.deleteActivity(activityId));
  assert(!defined(store.getState().activities[options.activityId]), `Failed to remove activityId "${activityId}"`);
}

export async function setTargetActivity(options = {}) {
  const activityId = getRequiredParam(options, 'activityId');
  const activity = getApp().store.getState().activities[options.activityId];

  assert(defined(activity), 'The specified activityId does not exist');
  assert(activity.focusable !== false, 'The specified activityId is not targetable');

  getApp().store.dispatch(Actions.setActiveActivity(activityId));
}

export async function toggleActivityVisibility(options = {}) {
  const activityId = getRequiredParam(options, 'activityId');
  assert(getApp().store.getState().activities[activityId], 'The specified activityId does not exist');

  getApp().store.dispatch(Actions.toggleActivityVisibility(options.activityId));
}

export async function addPoints(options = {}) {
  const points = getRequiredParam(options, 'points');
  const activityId = getRequiredParam(options, 'activityId');
  const activity = getApp().store.getState().activities[activityId];
  assert(defined(activity), 'The specified activityId does not exist');
  assert(Array.isArray(points), 'The "points" parameter must be an array');

  // Validate that the options satifies the Vera schema
  // @NOTE: Only the first point is checked, for performance reasons
  const result = validateGpsPoint('height', 'time')(points[0]);
  assert(result === true, `Invalid Point parameters: ${result}`);

  // Convert times and coordinates
  points.forEach(p => {
    p.time = ISOtoMicroseconds(p.time);
    p.latitude = convertToRadian(p.latitude);
    p.longitude = convertToRadian(p.longitude);
  });

  // Once the points have been fully loaded, resolve or reject
  await getApp().trackLoader.addPoints(activity, points, pointData => {
    getApp().onPointsAdded(activityId, pointData);
  });

  // Return nothing
}

/* Clock **************************************************************************************************************/
// @TODO: We really need to not use ISOtoMicroseconds (it's milliseconds anyway!). The date/time is parsed in the local
//        timezone. Here's the MDN note:
//        "Note: parsing of date strings with the Date constructor (and Date.parse, they are equivalent) is strongly
//        discouraged due to browser differences and inconsistencies. Support for RFC 2822 format strings is by
//        convention only. Support for ISO 8601 formats differs in that date-only strings (e.g. "1970-01-01") are
//        treated as UTC, not local.
export async function setTime(options = {}) {
  const isoTime = getRequiredParam(options, 'time');
  const time = ISOtoMicroseconds(isoTime);
  assert(!Number.isNaN(time), `The specified time "${isoTime}" is not valid`);

  getApp().store.dispatch(Actions.setTime(time));
}

export async function getTime() {
  const epochTime = getApp().store.getState().clock.time;
  return { time: epochTime, ISOTime: new Date(epochTime).toISOString() };
}

export async function setPlaybackSpeed(options = {}) {
  const speed = getRequiredParam(options, 'speed');
  assert(speed !== 0.0, 'Speed must be greater than zero. Use pause instead.');
  assert(Number.isFinite(speed) && speed > 0, 'Speed must be a positive finite number');
  getApp().store.dispatch(Actions.setClockSpeed(speed));
}

export async function play() {
  getApp().store.dispatch(Actions.startPlaying());
}

export async function pause() {
  getApp().store.dispatch(Actions.stopPlaying());
}

/* Media **************************************************************************************************************/
export async function addMedia(options = {}) {
  const media = getApp().media;

  // Validate that the options satifies the Vera schema
  const result = validateMedia(options);
  assert(result === true, `Invalid Media parameters: ${result}`);

  const mediaId = media.add(options);
  return { mediaId };
}

export async function deleteMedia(options = {}) {
  const mediaId = getRequiredParam(options, 'mediaId');
  getApp().media.destroy(mediaId);
}

export async function toggleMediaVisibility() {
  throw new Error('Media visibility toggling has been deprecated');
}

/* Options ************************************************************************************************************/
export async function setUnits(options = {}) {
  const units = getRequiredParam(options, 'units');
  assert(units === 'metric' || units === 'imperial', 'Invalid units. The options are "metric" and "imperial"');
  getApp().store.dispatch(Actions.setUnits(units));
}

export async function setCameraDistance(options = {}) {
  const distance = getRequiredParam(options, 'distance');
  assert(typeof distance === 'number' && !Number.isNaN(distance), 'The distance property must be a valid number');
  assert(distance > 0.0 && distance < Infinity, 'The distance property must be greater than zero and finite');
  getApp().store.dispatch(Actions.setCameraDistance(distance));
}

export async function setFullscreen(options = {}) {
  const fullscreen = getRequiredParam(options, 'fullscreen');
  assert(typeof fullscreen === 'boolean');
  getApp().ui.setFullscreen(fullscreen);
}

/**************************************************************************************************************
/* Helpers
/*************************************************************************************************************/
function getRequiredParam(object, param) {
  const result = object[param];
  if (!defined(result)) throw new Error(`Parameter ${param} is required`);
  return result;
}

function assert(x, message = 'Assert failed') {
  if (!x) throw new Error(message);
}
